<template>
  <div class="editor">
    <editor-menu-bar v-slot="{ commands, isActive }" :editor="editor">
      <div class="menubar">
        <span v-for="actionName in activeButtons" :key="actionName">
          <button
            v-if="actionName === 'bold'"
            class="menubar__button"
            :class="{ 'is-active': isActive.bold() }"
            @click="commands.bold"
          >
            <icon name="bold" />
          </button>
          <button
            v-if="actionName === 'italic'"
            class="menubar__button"
            :class="{ 'is-active': isActive.italic() }"
            @click="commands.italic"
          >
            <icon name="italic" />
          </button>

          <button
            v-if="actionName === 'strike'"
            class="menubar__button"
            :class="{ 'is-active': isActive.strike() }"
            @click="commands.strike"
          >
            <icon name="strike" />
          </button>

          <button
            v-if="actionName === 'underline'"
            class="menubar__button"
            :class="{ 'is-active': isActive.underline() }"
            @click="commands.underline"
          >
            <icon name="underline" />
          </button>

          <button
            v-if="actionName === 'code'"
            class="menubar__button"
            :class="{ 'is-active': isActive.code() }"
            @click="commands.code"
          >
            <icon name="code" />
          </button>

          <button
            v-if="actionName === 'paragraph'"
            class="menubar__button"
            :class="{ 'is-active': isActive.paragraph() }"
            @click="commands.paragraph"
          >
            <icon name="paragraph" />
          </button>

          <button
            v-if="actionName === 'h1'"
            class="menubar__button"
            :class="{ 'is-active': isActive.heading({ level: 1 }) }"
            @click="commands.heading({ level: 1 })"
          >
            H1
          </button>

          <button
            v-if="actionName === 'h2'"
            class="menubar__button"
            :class="{ 'is-active': isActive.heading({ level: 2 }) }"
            @click="commands.heading({ level: 2 })"
          >
            H2
          </button>

          <button
            v-if="actionName === 'h3'"
            class="menubar__button"
            :class="{ 'is-active': isActive.heading({ level: 3 }) }"
            @click="commands.heading({ level: 3 })"
          >
            H3
          </button>

          <button
            v-if="actionName === 'bullet_list'"
            class="menubar__button"
            :class="{ 'is-active': isActive.bullet_list() }"
            @click="commands.bullet_list"
          >
            <icon name="ul" />
          </button>

          <button
            v-if="actionName === 'ordered_list'"
            class="menubar__button"
            :class="{ 'is-active': isActive.ordered_list() }"
            @click="commands.ordered_list"
          >
            <icon name="ol" />
          </button>

          <button
            v-if="actionName === 'blockquote'"
            class="menubar__button"
            :class="{ 'is-active': isActive.blockquote() }"
            @click="commands.blockquote"
          >
            <icon name="quote" />
          </button>

          <button
            v-if="actionName === 'code_block'"
            class="menubar__button"
            :class="{ 'is-active': isActive.code_block() }"
            @click="commands.code_block"
          >
            <icon name="code" />
          </button>

          <button
            v-if="actionName === 'horizontal_rule'"
            class="menubar__button"
            @click="commands.horizontal_rule"
          >
            <icon class="custom-hr" name="hr" />
          </button>

          <button
            v-if="actionName === 'undo'"
            class="menubar__button"
            @click="commands.undo"
          >
            <icon name="undo" />
          </button>

          <button
            v-if="actionName === 'redo'"
            class="menubar__button"
            @click="commands.redo"
          >
            <icon name="redo" />
          </button>
        </span>
      </div>
    </editor-menu-bar>

    <!-- Editor Textarea -->
    <div class="editer_form">
      <div class="editer_text_wrap n_line">
        <div class="editer_input_t" style="width: 100%; height: 266px;">
          <editor-content class="editor__content" :editor="editor" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Icon from '@/components/common/TipTap/Icon'
import { Editor, EditorContent, EditorMenuBar } from 'tiptap'
import {
  Blockquote,
  CodeBlock,
  HardBreak,
  Heading,
  HorizontalRule,
  OrderedList,
  BulletList,
  ListItem,
  TodoItem,
  TodoList,
  Bold,
  Code,
  Italic,
  Link,
  Strike,
  Underline,
  History
} from 'tiptap-extensions'

export default {
  name: 'TipTap',
  components: {
    EditorMenuBar,
    Icon,
    EditorContent
  },
  props: {
    initialContent: {
      type: String,
      default: null
    },
    activeButtons: {
      type: Array,
      validator: function (list) {
        for (let el of list) {
          // The value must match one of these strings
          if (
            [
              'bold',
              'italic',
              'strike',
              'underline',
              'code',
              'paragraph',
              'h1',
              'h2',
              'h3',
              'bullet_list',
              'ordered_list',
              'blockquote',
              'code_block',
              'horizontal_rule',
              'undo',
              'redo'
            ].indexOf(el) === -1
          ) {
            return -1
          }
        }
        return 1
      },
      default: () => ['bold', 'italic']
    }
  },
  data() {
    const content = this.initialContent
    return {
      html: '',
      json: '',
      editor: new Editor({
        extensions: [
          new Blockquote(),
          new BulletList(),
          new CodeBlock(),
          new HardBreak(),
          new Heading({ levels: [1, 2, 3] }),
          new HorizontalRule(),
          new ListItem(),
          new OrderedList(),
          new TodoItem(),
          new TodoList(),
          new Link(),
          new Bold(),
          new Code(),
          new Italic(),
          new Strike(),
          new Underline(),
          new History()
        ],
        content
      })
    }
  },
  beforeDestroy() {
    this.editor.destroy()
  },
  created() {
    this.editor.on('update', () => {
      this.$emit('update', this.editor.getHTML())
    })
  }
}
</script>

<style lang="scss" scoped>
.menubar__button {
  margin: 5px;
  min-width: 25px;
  padding: 2px 8px 5px;
  border-radius: 4px;
  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
  }
  .icon {
    width: 15px;
  }
}

.custom-hr {
  margin-top: -5px !important;
  margin-left: -10px !important;
}
</style>

<style lang="scss">
//Need Non-scoped to control ProseMirror
.editor__content {
  .ProseMirror {
    height: 250px;
  }
  .ProseMirror:focus {
    outline: none;
  }
}
</style>
